import { default as VueClass } from 'vue';
import { ApplicationException } from '@pjn/core/error';
import { addGeneralFilters } from '../filters';
import { createRouter } from '../router';
import VueRouter from 'vue-router';
import { extendVue } from '../extensions';
import { addLibs } from '../libs';
/**
 * Inicializa la configuracion de la aplicacion en base a los parametros especificados.
 *
 * Esta inicializacion incluye:
 *  - Registrar los filtros generales y los plugins especificados.
 *  - Incluye el router en caso de existir.
 *  - Comprobar la seguridad antes de iniciar la app.
 *
 * @param Vue Clase Vue
 * @param params parametros de configuracion de la aplicacion
 * @returns La configuracion (inicializada) a utilizar para crear la instancia principal de Vue
 */
async function initVueConfig(Vue, params) {
    Vue.config.productionTip = false;
    // Si se definio un Store, se llama a su metodo init primero.
    if (params.store && params.store.init) {
        console.info('Inicializando Store (init)');
        await params.store.init();
    }
    if (params.plugins) {
        console.debug('Se incluyen los siguientes plugins de Vue %o', Object.keys(params.plugins));
        Object.values(params.plugins).forEach(p => Vue.use(p));
    }
    // Si no se especifico elementos a inyectar, se crea mapa vacio
    if (!params.provide)
        params.provide = {};
    // Si esta disponible, se inyecta el uiStore
    if (params.store && params.store.ui)
        params.provide.uiStore = params.store.ui;
    // Si hay seguridad, se inicializa y se hace login si es requerido
    if (params.security && params.security.authentication) {
        const authResult = await checkSecurity(params.security.authentication, params.security.authStore);
        // Si se definio un listener para despues del login se llama, y se espera su promise
        if (params.security.onFirstLogin)
            await params.security.onFirstLogin(authResult);
        // Si se definio un Store, se llama a su metodo de init despues del login
        if (params.store && params.store.initAfterSecurity) {
            console.info('Inicializando Store luego de login (initAfterSecurity)');
            await params.store.initAfterSecurity(authResult);
        }
        // Se inyecta authStore para usar donde sea necesario
        params.provide.authStore = params.security.authStore;
    }
    if (params.components) {
        console.debug('Se incluyen los componentes globales %o', Object.keys(params.components));
        Object.entries(params.components).forEach(e => Vue.component(e[0], e[1]));
    }
    addGeneralFilters(Vue);
    addLibs(Vue);
    // Se configura el router en base a la config o se incluye el especificado.
    let router = undefined;
    if (params.router || params.routes) {
        Vue.use(VueRouter);
        // Si el router se paso por parametro, se usa. Si se paso la config, se lo debe construir.
        router = params.router instanceof VueRouter ? params.router : createRouter((params.router || params.routes), params.security);
        console.debug('Se utilizara router:', router);
    }
    return {
        router,
        render: h => h(params.root, params.rootConfig || {}),
        provide: {
            ...(params.provide || {}),
            APP_TITLE: params.title,
            menuItems: params.menuItems || []
        },
        ...(params.extra || {})
    };
}
/**
 * Inicializa la aplicacion Vue en base a los parametros especificados.
 *
 * Esta inicializacion incluye:
 *  - Registrar los filtros generales y los plugins especificados.
 *  - Incluye el router en caso de existir.
 *  - Comprobar la seguridad antes de iniciar la app.
 *  - Finalmente, crea la instancia de la aplicacion y la devuelve.
 *
 * @param Vue Clase Vue
 * @param params parametros de configuracion de la aplicacion
 * @returns La instancia de la aplicacion creada.
 */
export async function start(params) {
    const Vue = VueClass;
    console.group('Inicializacion');
    console.time('Inicializacion');
    console.info('Inicializando aplicacion Vue');
    // Se extiende el prototype de Vue para agregar properties a todos los componentes
    extendVue(Vue);
    // Se inicializa y se crea la configuracion
    const config = await initVueConfig(Vue, params);
    // Se crea la instancia Vue de la App y se monta en el DOM
    const app = new Vue(config);
    app.$mount('#app');
    console.info('App creada y montada en %o', document.getElementById('app'));
    console.timeEnd('Inicializacion');
    console.groupEnd();
    return app;
}
/**
 * Comprueba la seguridad antes de iniciar la aplicacion.
 *
 * Puede exigir autenticacion o bien solo comprobar si ya esta autenticado pero permitir acceso
 * a los anonimos tambien.
 *
 * @param authentication tipo de chequeo de autenticacion
 * @param authStore store que maneja la seguridad.
 */
function checkSecurity(authentication, authStore) {
    if (!authStore)
        throw new ApplicationException('Para utilizar autenticacion debe proveer un AuthStore');
    if (authentication === 'check') {
        console.debug('Se comprueba el estado de autenticacion (SIN forzar login)');
        return authStore.loginIfAuthenticated();
    }
    else if (authentication === 'required') {
        console.debug('Se requiere autenticacion (se fuerza login)');
        return authStore.login();
    }
    else {
        throw new ApplicationException('Tipo de autenticacion invalida: debe ser required o check');
    }
}
//# sourceMappingURL=index.js.map